import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Info, X } from 'lucide-react';
import { RATINGS, RATING_CRITERIA } from '../constants';
import usePopoverAnchor from './usePopoverAnchor';

const TIER_DOT = {
    Exceptional: 'bg-green-500',
    Strong:      'bg-green-400',
    Moderate:    'bg-blue-500',
    Weak:        'bg-orange-500',
    Critical:    'bg-red-600',
};

/**
 * Anchored popover showing the 5 rating definitions for one variable.
 * Click outside or Esc to dismiss. Content pulled from RATING_CRITERIA
 * which mirrors the threshold rules in the backend weights config.
 */
export default function RatingCriteriaPopover({ variableKey, label, anchorRef, open, onClose }) {
    const popoverRef = useRef(null);
    const criteria = RATING_CRITERIA[variableKey];
    const positionStyle = usePopoverAnchor(anchorRef, open, { preferredWidth: 320 });

    useEffect(() => {
        if (!open) return;
        const handleClickOutside = (e) => {
            if (popoverRef.current && popoverRef.current.contains(e.target)) return;
            if (anchorRef?.current && anchorRef.current.contains(e.target)) return;
            onClose?.();
        };
        const handleEsc = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEsc);
        };
    }, [open, onClose, anchorRef]);

    if (!open || !criteria) return null;

    return createPortal(
        <div ref={popoverRef}
             style={positionStyle}
             className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg ring-1 ring-black/5">
            <div className="flex items-start justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800">
                <div className="min-w-0">
                    <div className="text-sm font-semibold text-slate-900 dark:text-white tracking-tight">{label} — Rating Criteria</div>
                    <div className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">measured by {criteria.unit}</div>
                </div>
                <button onClick={onClose} className="p-0.5 -mr-1 -mt-0.5 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors">
                    <X size={14} />
                </button>
            </div>

            <ul className="px-4 py-3 space-y-2.5">
                {RATINGS.map(r => (
                    <li key={r} className="flex items-start gap-2.5">
                        <span className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${TIER_DOT[r]}`} />
                        <div className="flex-1 min-w-0">
                            <span className="text-xs font-semibold text-slate-800 dark:text-slate-100">{r}</span>
                            <span className="ml-1.5 text-xs text-slate-600 dark:text-slate-300 leading-relaxed">{criteria[r]}</span>
                        </div>
                    </li>
                ))}
            </ul>
        </div>,
        document.body
    );
}

/** Trigger button — pair with the popover above */
export function RatingCriteriaButton({ onClick, buttonRef }) {
    return (
        <button ref={buttonRef} type="button" onClick={onClick}
                className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-blue-600 dark:text-slate-400 dark:hover:text-blue-400 transition-colors">
            <Info size={12} /> View rating criteria
        </button>
    );
}
